// =====================================================
// 白板系统 - 标签页状态管理 (Zustand)
// LRU 缓存策略，最多保留 MAX_TABS 个标签页
// 支持 localStorage 持久化、未保存标记、拖拽排序
// =====================================================

import { create } from 'zustand';

const MAX_TABS = 10;
const STORAGE_KEY = 'whiteboard_open_tabs_v1';

export interface WhiteboardTab {
  /** 白板项目 ID */
  id: string;
  /** 显示名称 */
  name: string;
  /** 打开时间（用于 LRU 淘汰） */
  openedAt: number;
  /** 最后激活时间（用于 LRU 淘汰） */
  lastActiveAt: number;
}

interface WhiteboardTabsState {
  /** 已打开的标签页列表（有序） */
  tabs: WhiteboardTab[];
  /** 当前激活的标签页 ID */
  activeTabId: string | null;
  /** 未保存状态的标签页映射 { [tabId]: boolean } */
  dirtyTabs: Record<string, boolean>;

  /** 打开（或激活已存在的）标签页 */
  openTab: (id: string, name: string) => void;
  /** 关闭标签页，返回下一个应激活的 tab ID */
  closeTab: (id: string) => string | null;
  /** 激活标签页 */
  activateTab: (id: string) => void;
  /** 更新标签页名称（白板重命名/初次异步加载时同步） */
  updateTabName: (id: string, name: string) => void;
  /** 设置标签页的未保存状态 */
  setTabDirty: (id: string, isDirty: boolean) => void;
  /** 拖拽重新排序标签页 */
  reorderTabs: (fromIndex: number, toIndex: number) => void;
  /** 关闭所有标签页 */
  closeAllTabs: () => void;
  /** 关闭除指定 ID 外的所有标签页 */
  closeOtherTabs: (id: string) => void;
  /** 关闭指定 ID 右侧的所有标签页 */
  closeRightTabs: (id: string) => void;
}

// 从 localStorage 加载已保存的标签页（容错处理）
function loadSavedTabs(): { tabs: WhiteboardTab[]; activeTabId: string | null } {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { tabs: [], activeTabId: null };
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed?.tabs)) {
      return {
        tabs: parsed.tabs.slice(0, MAX_TABS),
        activeTabId: typeof parsed.activeTabId === 'string' ? parsed.activeTabId : null,
      };
    }
  } catch (e) {
    console.warn('[WhiteboardTabs] 无法从本地缓存恢复标签页:', e);
  }
  return { tabs: [], activeTabId: null };
}

// 保存标签页状态到 localStorage
function persistTabs(tabs: WhiteboardTab[], activeTabId: string | null) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ tabs, activeTabId }));
  } catch (e) {
    console.warn('[WhiteboardTabs] 保存标签页到本地失败:', e);
  }
}

const initialSaved = loadSavedTabs();

export const useWhiteboardTabsStore = create<WhiteboardTabsState>((set, get) => ({
  tabs: initialSaved.tabs,
  activeTabId: initialSaved.activeTabId,
  dirtyTabs: {},

  openTab: (id, name) => {
    const { tabs } = get();
    const existing = tabs.find((t) => t.id === id);

    if (existing) {
      // 已打开：激活并更新最后访问时间（如果新名称不是“加载中...”，更新名称）
      const nextName = name && name !== '加载中...' ? name : existing.name;
      const newTabs = tabs.map((t) =>
        t.id === id ? { ...t, lastActiveAt: Date.now(), name: nextName } : t
      );
      set({ tabs: newTabs, activeTabId: id });
      persistTabs(newTabs, id);
      return;
    }

    const now = Date.now();
    const newTab: WhiteboardTab = { id, name: name || '未命名白板', openedAt: now, lastActiveAt: now };
    let newTabs = [...tabs, newTab];

    // LRU 淘汰：超出最大数量时移除最久未激活的标签页
    if (newTabs.length > MAX_TABS) {
      const evictCandidates = newTabs
        .filter((t) => t.id !== id)
        .sort((a, b) => a.lastActiveAt - b.lastActiveAt);

      const toEvict = new Set<string>();
      while (newTabs.length - toEvict.size > MAX_TABS && evictCandidates.length > 0) {
        const victim = evictCandidates.shift();
        if (victim) toEvict.add(victim.id);
      }
      newTabs = newTabs.filter((t) => !toEvict.has(t.id));
    }

    set({ tabs: newTabs, activeTabId: id });
    persistTabs(newTabs, id);
  },

  closeTab: (id) => {
    const { tabs, activeTabId, dirtyTabs } = get();
    const idx = tabs.findIndex((t) => t.id === id);
    if (idx === -1) return null;

    const newTabs = tabs.filter((t) => t.id !== id);

    // 如果关闭的是当前激活的标签，激活相邻标签
    let nextActiveId: string | null = activeTabId;
    if (activeTabId === id) {
      if (newTabs.length === 0) {
        nextActiveId = null;
      } else if (idx < newTabs.length) {
        nextActiveId = newTabs[idx].id;
      } else {
        nextActiveId = newTabs[newTabs.length - 1].id;
      }
    }

    const nextDirtyTabs = { ...dirtyTabs };
    delete nextDirtyTabs[id];

    set({ tabs: newTabs, activeTabId: nextActiveId, dirtyTabs: nextDirtyTabs });
    persistTabs(newTabs, nextActiveId);
    return nextActiveId;
  },

  activateTab: (id) => {
    const { tabs } = get();
    const newTabs = tabs.map((t) =>
      t.id === id ? { ...t, lastActiveAt: Date.now() } : t
    );
    set({ tabs: newTabs, activeTabId: id });
    persistTabs(newTabs, id);
  },

  updateTabName: (id, name) => {
    if (!name || name === '加载中...') return;
    const { tabs, activeTabId } = get();
    const newTabs = tabs.map((t) => (t.id === id ? { ...t, name } : t));
    set({ tabs: newTabs });
    persistTabs(newTabs, activeTabId);
  },

  setTabDirty: (id, isDirty) => {
    const { dirtyTabs } = get();
    if (!!dirtyTabs[id] === isDirty) return;
    set({
      dirtyTabs: {
        ...dirtyTabs,
        [id]: isDirty,
      },
    });
  },

  reorderTabs: (fromIndex, toIndex) => {
    const { tabs, activeTabId } = get();
    if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0 || fromIndex >= tabs.length || toIndex >= tabs.length) {
      return;
    }
    const nextTabs = [...tabs];
    const [moved] = nextTabs.splice(fromIndex, 1);
    nextTabs.splice(toIndex, 0, moved);
    set({ tabs: nextTabs });
    persistTabs(nextTabs, activeTabId);
  },

  closeAllTabs: () => {
    set({ tabs: [], activeTabId: null, dirtyTabs: {} });
    persistTabs([], null);
  },

  closeOtherTabs: (id) => {
    const { tabs, dirtyTabs } = get();
    const newTabs = tabs.filter((t) => t.id === id);
    const nextDirtyTabs: Record<string, boolean> = {};
    if (dirtyTabs[id]) nextDirtyTabs[id] = true;

    set({
      tabs: newTabs,
      activeTabId: id,
      dirtyTabs: nextDirtyTabs,
    });
    persistTabs(newTabs, id);
  },

  closeRightTabs: (id) => {
    const { tabs, activeTabId, dirtyTabs } = get();
    const idx = tabs.findIndex((t) => t.id === id);
    if (idx === -1) return;

    const newTabs = tabs.slice(0, idx + 1);
    const toRemoveIds = new Set(tabs.slice(idx + 1).map((t) => t.id));
    const nextDirtyTabs = { ...dirtyTabs };
    toRemoveIds.forEach((rid) => delete nextDirtyTabs[rid]);

    let nextActiveId = activeTabId;
    if (activeTabId && toRemoveIds.has(activeTabId)) {
      nextActiveId = id;
    }

    set({ tabs: newTabs, activeTabId: nextActiveId, dirtyTabs: nextDirtyTabs });
    persistTabs(newTabs, nextActiveId);
  },
}));
